import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, Clock, CheckCircle2, Plus, Minus, Building2, Scale, Users } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactSection from "../components/ContactSection";
import PracticeAreas from "../components/PracticeAreas";
import { useSEO, useJsonLd, SITE } from "../lib/seo";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/a9778dd3-cc77-4862-94cf-6a30e83d1c8c/images/92649c30fb8195c6e2cf37183ce091f5570bd6f52a7e5b54ec32fe7a1d139edb.png";

export default function LocationPage({ city }) {
    const [openFaq, setOpenFaq] = useState(0);

    useSEO({
        title: `Avvocato Diritto del Lavoro ${city.name}`,
        description: city.metaDescription,
        path: city.path,
    });

    // JSON-LD: Breadcrumb + LocalBusiness branch + FAQPage
    useJsonLd(
        [
            {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE.url}/` },
                    { "@type": "ListItem", "position": 2, "name": "Sedi", "item": `${SITE.url}/#contatti` },
                    { "@type": "ListItem", "position": 3, "name": city.name, "item": `${SITE.url}${city.path}` },
                ],
            },
            {
                "@context": "https://schema.org",
                "@type": "LegalService",
                "@id": `${SITE.url}${city.path}#branch`,
                "name": `Studio Legale Avv. Marco Candido — ${city.name}`,
                "url": `${SITE.url}${city.path}`,
                "telephone": city.tel,
                "areaServed": [
                    { "@type": "City", "name": city.name },
                    ...(city.areaServed || []).map((c) => ({ "@type": "City", "name": c })),
                ],
                "address": {
                    "@type": "PostalAddress",
                    "addressLocality": city.name,
                    "addressRegion": city.region,
                    "addressCountry": "IT",
                },
                "parentOrganization": { "@id": `${SITE.url}/#legalservice` },
            },
            ...(city.faqs
                ? [
                      {
                          "@context": "https://schema.org",
                          "@type": "FAQPage",
                          "mainEntity": city.faqs.map((f) => ({
                              "@type": "Question",
                              "name": f.q,
                              "acceptedAnswer": { "@type": "Answer", "text": f.a },
                          })),
                      },
                  ]
                : []),
        ],
        "location-jsonld",
    );

    return (
        <div data-testid={`location-page-${city.slug}`}>
            <Header />
            <main>

            {/* Hero */}
            <section className="relative min-h-[80vh] flex items-center bg-navy overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center opacity-90" style={{ backgroundImage: `url(${HERO_BG})` }} />
                <div className="absolute inset-0 bg-navy/85" />
                <div className="absolute top-0 bottom-0 left-10 lg:left-24 w-px bg-gold/30 hidden md:block" />

                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 w-full">
                    <div className="max-w-4xl">
                        <div className="flex items-center gap-4 mb-8">
                            <span className="block w-16 h-[2px] bg-gold" />
                            <span className="label-eyebrow !text-gold">Sede {city.region}</span>
                        </div>
                        <h1 className="font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-6">
                            Avvocato Diritto del Lavoro<br />
                            <span className="italic text-gold">{city.name}</span>
                        </h1>
                        <p className="text-slate-200 text-lg md:text-xl leading-relaxed max-w-3xl mb-12">
                            {city.intro}
                        </p>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <Link to={`${city.path}#contatti`} data-testid={`location-cta-${city.slug}`} className="btn-gold group">
                                Richiedi un appuntamento
                                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <a href={`tel:${city.tel.replace(/[\s-]/g, "")}`} className="inline-flex items-center gap-2 px-7 py-3 border border-white/30 text-white font-semibold tracking-wide text-sm transition-all duration-300 hover:border-gold hover:text-gold">
                                <Phone size={16} /> {city.tel}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Local info */}
            <section className="py-24 lg:py-32 bg-[#FAFAFA]">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    {city.body && city.body.length > 0 && (
                        <>
                            <div className="flex items-center gap-4 mb-6">
                                <span className="gold-rule" />
                                <span className="label-eyebrow">Lo Studio a {city.name}</span>
                            </div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.1] mb-10 max-w-3xl">
                                {city.title}
                            </h2>
                        </>
                    )}

                    <div className={city.body && city.body.length > 0 ? "grid grid-cols-1 lg:grid-cols-12 gap-12" : "flex justify-center"}>
                        {city.body && city.body.length > 0 && (
                            <div className="lg:col-span-7 space-y-6 text-slate-700 text-base md:text-lg leading-relaxed">
                                {city.body.map((p, i) => (
                                    <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                                ))}
                            </div>
                        )}

                        {/* Quick facts card */}
                        <div className={city.body && city.body.length > 0 ? "lg:col-span-5" : "w-full max-w-xl"}>
                            <div className="bg-navy text-white p-8 lg:p-10 relative">
                                <div className="absolute -top-px left-0 w-24 h-[2px] bg-gold" />
                                <div className="label-eyebrow mb-6">Sede di {city.name}</div>
                                <ul className="space-y-5 text-sm">
                                    <li className="flex items-start gap-3">
                                        <MapPin size={16} className="text-gold mt-1 flex-shrink-0" />
                                        <span>{city.address}</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Phone size={16} className="text-gold mt-1 flex-shrink-0" />
                                        <a href={`tel:${city.tel.replace(/[\s-]/g, "")}`} className="hover:text-gold">{city.tel}</a>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Mail size={16} className="text-gold mt-1 flex-shrink-0" />
                                        <a href="mailto:marco.candido@pabclex.it" className="hover:text-gold break-all">marco.candido@pabclex.it</a>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Clock size={16} className="text-gold mt-1 flex-shrink-0" />
                                        <span>{city.hours}</span>
                                    </li>
                                </ul>
                                <div className="mt-8 pt-6 border-t border-white/10">
                                    <div className="text-xs text-gold uppercase tracking-[0.25em] font-bold mb-3">Aree principali</div>
                                    <ul className="space-y-2">
                                        {city.services.map((s, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                                <CheckCircle2 size={14} className="text-gold mt-0.5 flex-shrink-0" />
                                                {s}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <PracticeAreas />

            {/* Sezione "In cosa siamo specializzati a [Città]" */}
            {city.specializations && (
                <section className="py-24 lg:py-32 bg-white">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Specializzazioni locali</span>
                        </div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.1] mb-12 max-w-3xl">
                            Le vertenze più frequenti a <span className="italic text-gold-deep">{city.name}</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {city.specializations.map((spec, i) => (
                                <div key={i} className="border-l-4 border-gold pl-6" data-testid={`spec-${i}`}>
                                    <h3 className="font-serif text-navy text-2xl mb-3 leading-tight">{spec.title}</h3>
                                    <p className="text-slate-700 text-base leading-relaxed">{spec.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Sezione "Perché scegliere lo Studio a [Città]" */}
            {city.reasons && (
                <section className="py-24 lg:py-32 bg-[#FAFAFA]">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                            <div className="lg:col-span-5">
                                <div className="flex items-center gap-4 mb-6">
                                    <span className="gold-rule" />
                                    <span className="label-eyebrow">Chi siamo a {city.name}</span>
                                </div>
                                <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.1] mb-6">
                                    Perché scegliere lo Studio a <span className="italic text-gold-deep">{city.name}</span>
                                </h2>
                                <p className="text-slate-600 text-base md:text-lg leading-relaxed">{city.reasonsIntro}</p>
                            </div>
                            <div className="lg:col-span-7 space-y-8">
                                {city.reasons.map((r, i) => (
                                    <div key={i} className="flex items-start gap-5" data-testid={`reason-${i}`}>
                                        <div className="flex-shrink-0 w-12 h-12 border border-navy/20 flex items-center justify-center bg-white">
                                            {i === 0 ? <Scale size={20} className="text-gold-deep" /> :
                                             i === 1 ? <Building2 size={20} className="text-gold-deep" /> :
                                             i === 2 ? <Users size={20} className="text-gold-deep" /> :
                                             <CheckCircle2 size={20} className="text-gold-deep" />}
                                        </div>
                                        <div>
                                            <h3 className="font-serif text-navy text-xl mb-2 leading-tight">{r.title}</h3>
                                            <p className="text-slate-600 text-base leading-relaxed">{r.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Sezione "Tribunale / Foro di [Città]" */}
            {city.tribunal && (
                <section className="py-24 lg:py-32 bg-white">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="gold-rule" />
                            <span className="label-eyebrow">Foro di {city.name}</span>
                        </div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.1] mb-8 max-w-3xl">
                            {city.tribunal.h2}
                        </h2>
                        <div className="prose prose-lg max-w-3xl text-slate-700 space-y-5">
                            {city.tribunal.paragraphs.map((p, i) => (
                                <p key={i} className="leading-[1.85]" dangerouslySetInnerHTML={{ __html: p }} />
                            ))}
                        </div>
                        {city.tribunal.address && (
                            <div className="mt-10 inline-flex items-start gap-4 p-6 border-l-4 border-gold bg-slate-50">
                                <MapPin size={20} className="text-gold-deep mt-1 flex-shrink-0" />
                                <div>
                                    <div className="font-serif text-navy text-lg mb-1">Tribunale del Lavoro di {city.name}</div>
                                    <div className="text-slate-600 text-sm">{city.tribunal.address}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* FAQ specifiche di città */}
            {city.faqs && (
                <section className="py-24 lg:py-32 bg-[#FAFAFA]">
                    <div className="max-w-5xl mx-auto px-6 lg:px-10">
                        <div className="max-w-3xl mb-14">
                            <div className="flex items-center gap-4 mb-6">
                                <span className="gold-rule" />
                                <span className="label-eyebrow">FAQ locali</span>
                            </div>
                            <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.1]">
                                Domande frequenti su <span className="italic text-gold-deep">{city.name}</span>
                            </h2>
                        </div>
                        <div className="divide-y divide-slate-200 border-y border-slate-200">
                            {city.faqs.map((f, i) => {
                                const isOpen = openFaq === i;
                                return (
                                    <div key={i} data-testid={`city-faq-${i}`}>
                                        <button
                                            onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                            aria-expanded={isOpen}
                                            className="w-full py-7 flex items-start justify-between gap-6 text-left group"
                                        >
                                            <h3 className="font-serif text-xl md:text-2xl text-navy group-hover:text-gold-deep leading-tight transition-colors">
                                                {f.q}
                                            </h3>
                                            <span className="flex-shrink-0 w-10 h-10 border border-navy/30 group-hover:border-gold group-hover:bg-gold flex items-center justify-center transition-all">
                                                {isOpen ? <Minus size={18} className="text-navy" /> : <Plus size={18} className="text-navy" />}
                                            </span>
                                        </button>
                                        <div className={`grid transition-all duration-500 ${isOpen ? "grid-rows-[1fr] opacity-100 pb-7" : "grid-rows-[0fr] opacity-0"}`}>
                                            <div className="overflow-hidden">
                                                <p className="text-slate-600 text-base md:text-lg leading-relaxed pr-16">{f.a}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            <ContactSection />
            </main>
            <Footer />
        </div>
    );
}

// City presets — Enhanced landing pages (~2500 parole ciascuna)
export const CITIES = {
    piacenza: {
        slug: "piacenza",
        name: "Piacenza",
        region: "Emilia-Romagna",
        path: "/avvocato-lavoro-piacenza",
        tel: "0523 468737",
        address: "Corso Vittorio Emanuele II, 142/D — 29121 Piacenza (PC)",
        hours: "Lun – Ven, 9:00 – 18:00",
        areaServed: ["Lodi", "Cremona", "Parma", "Pavia"],
        intro:
            "Lo Studio ha la sua sede principale a Piacenza, dove assiste lavoratori, dirigenti e aziende dell'Emilia-Romagna e delle province limitrofe in materia di diritto del lavoro. Prima consulenza sempre gratuita.",
        title: "Assistenza giuslavoristica presso la sede di Piacenza.",
        metaDescription:
            "Avvocato giuslavorista a Piacenza: licenziamenti, mobbing, vertenze, contratti di lavoro. Studio dell'Avv. Marco Candido, Corso Vittorio Emanuele II 142/D. Tel 0523 468737.",
        body: [
            "La sede di <strong>Piacenza</strong> in Corso Vittorio Emanuele II 142/D è la sede principale dello Studio dell'Avv. Marco Candido. È qui che vengono gestite la gran parte delle pratiche e dove i clienti vengono ricevuti per il primo incontro conoscitivo, sempre <strong>gratuito</strong>.",
            "Lo Studio opera nel Foro di Piacenza e nei fori limitrofi (Lodi, Cremona, Parma, Pavia), avvalendosi della collaborazione di colleghi esperti nel diritto del lavoro e in altre materie trasversali. La conoscenza approfondita del <strong>tessuto produttivo emiliano</strong> — dalla logistica alla meccanica, dall'alimentare al terziario — permette un approccio pragmatico tanto al lavoratore quanto all'azienda.",
            "L'Avv. Candido si occupa esclusivamente di diritto del lavoro e cura personalmente ogni vertenza: dalla fase stragiudiziale al giudizio davanti al Tribunale del Lavoro, dall'assistenza sindacale alla consulenza continuativa alle imprese.",
        ],
        services: [
            "Licenziamenti illegittimi",
            "Mobbing e demansionamento",
            "Vertenze di lavoro",
            "Assistenza continuativa alle imprese",
        ],
        specializations: [
            {
                title: "Licenziamenti nel distretto piacentino",
                desc: "Impugnazione di licenziamenti disciplinari, per giustificato motivo oggettivo e collettivi, con particolare esperienza nel settore logistica (uno dei più sviluppati d'Italia proprio a Piacenza), meccanica e alimentare. Assistenza nei 60 giorni obbligatori per l'impugnazione.",
            },
            {
                title: "Vertenze presso l'ITL di Piacenza",
                desc: "L'Ispettorato Territoriale del Lavoro di Piacenza (competente anche per Lodi) è il primo interlocutore per le conciliazioni stragiudiziali. Lo Studio segue costantemente le procedure di conciliazione ex art. 410 c.p.c.",
            },
            {
                title: "Mobbing e straining",
                desc: "Assistenza specialistica nella raccolta prove documentali, testimoniali e mediche. Collaborazione consolidata con medici legali e psichiatri di fiducia per la costruzione del compendio probatorio davanti al Tribunale di Piacenza.",
            },
            {
                title: "Diritto sindacale e ristrutturazioni aziendali",
                desc: "Assistenza a aziende del territorio piacentino in operazioni di riorganizzazione, procedure di licenziamento collettivo ex L. 223/1991, trattative sindacali e accordi di secondo livello nel settore metalmeccanico e logistico.",
            },
        ],
        reasonsIntro: "L'Avv. Marco Candido è presente sul territorio piacentino con la sede storica dello Studio ed è specializzato esclusivamente nel diritto del lavoro. Ecco cosa distingue il nostro approccio.",
        reasons: [
            {
                title: "Esclusivamente diritto del lavoro",
                desc: "Non ci occupiamo di altre materie: questa specializzazione ci permette di conoscere ogni sfumatura della normativa (Statuto Lavoratori, Jobs Act, CCNL di settore) e di anticipare gli sviluppi giurisprudenziali della Cassazione e delle Corti d'Appello.",
            },
            {
                title: "Radicamento nel territorio",
                desc: "L'esperienza pluriennale sul territorio consente una conoscenza approfondita delle realtà imprenditoriali e degli aspetti locali più critici del mondo del lavoro.",
            },
            {
                title: "Assistenza a lavoratori e aziende",
                desc: "L'esperienza maturata su entrambi i fronti — dipendenti da un lato, datori dall'altro — ci fornisce una prospettiva strategica unica nelle trattative e nelle conciliazioni stragiudiziali, spesso risolutive in tempi rapidi.",
            },
            {
                title: "Prima consulenza gratuita",
                desc: "L'incontro conoscitivo iniziale è sempre gratuito. Prima di qualsiasi attività l'Avvocato fornisce un parere trasparente sulla fondatezza della pretesa e un preventivo forense chiaro. Sono possibili modalità di pagamento agevolate.",
            },
        ],
        tribunal: {
            h2: "Il Tribunale del Lavoro di Piacenza",
            paragraphs: [
                "Il <strong>Tribunale del Lavoro di Piacenza</strong> — sezione specializzata del Tribunale Ordinario — è competente per tutte le controversie individuali di lavoro e previdenza sui rapporti sorti nel territorio provinciale. Il ricorso è disciplinato dal rito del lavoro (art. 409 e ss. c.p.c.) per le controversie ordinarie, e dal cd. rito Fornero (L. 92/2012) o dal rito ordinario per le controversie da licenziamento in base al regime applicabile.",
                "Le cause di lavoro presentano tempi generalmente più rapidi rispetto al civile ordinario: dalle 4 alle 12 mesi in primo grado, a seconda della complessità istruttoria. Piacenza, per dimensioni ridotte del contenzioso, offre <strong>tempi mediamente più brevi</strong> rispetto ai grandi tribunali italiani come Milano o Roma.",
                "Le decisioni del Tribunale di Piacenza sono appellabili davanti alla <strong>Corte d'Appello di Bologna</strong> (Sezione Lavoro). Lo Studio segue anche le cause di legittimità davanti alla Corte di Cassazione — Sezione Lavoro. La conoscenza degli orientamenti locali della sezione lavoro consente di predisporre strategie difensive più efficaci.",
                "Molte controversie vengono risolte prima del giudizio grazie alla conciliazione presso l'<strong><a href='/articolo/vertenze-di-lavoro-a-piacenza-come-funziona-la-conciliazione-presso-litl' class='text-gold-deep underline hover:no-underline'>ITL di Piacenza</a></strong> o presso le sedi sindacali competenti (CGIL, CISL, UIL, UGL), con l'assistenza obbligatoria di un avvocato per la validità del verbale come titolo esecutivo.",
            ],
            address: "Via Consiglio, 17 — 29121 Piacenza (PC) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Dove si trova lo Studio Legale Candido a Piacenza?",
                a: "La sede principale è in Corso Vittorio Emanuele II 142/D, 29121 Piacenza (PC), in pieno centro cittadino e comodamente raggiungibile da tutte le zone della provincia. Il primo incontro conoscitivo è sempre gratuito, previo appuntamento telefonico al numero 0523 468737 o via email.",
            },
            {
                q: "Quali sono le aree di specializzazione a Piacenza?",
                a: "Lo Studio si occupa esclusivamente di diritto del lavoro. Le materie più trattate sono: licenziamenti individuali e collettivi, mobbing e demansionamento, vertenze retributive, contratti di lavoro (subordinato, apprendistato, a termine, dirigenza), diritto sindacale, conciliazioni stragiudiziali presso ITL e sedi sindacali, contenzioso previdenziale INPS/INAIL.",
            },
            {
                q: "Lo Studio Candido opera anche fuori dalla provincia di Piacenza?",
                a: "Sì. Lo Studio segue costantemente cause davanti al Tribunale di Piacenza, Lodi, Cremona, Parma e Pavia (le province confinanti), oltre alla Corte d'Appello di Bologna. Le altre sedi operative dello Studio sono a Milano e Lecce.",
            },
            {
                q: "Quanto tempo ho per impugnare un licenziamento a Piacenza?",
                a: "Il termine è di 60 giorni dalla ricezione del licenziamento per l'impugnazione stragiudiziale (raccomandata A/R, PEC), seguito da 180 giorni per il deposito del ricorso al Tribunale del Lavoro di Piacenza o per l'attivazione della conciliazione. Sono termini perentori: rivolgiti tempestivamente allo Studio.",
            },
            {
                q: "Il primo incontro è davvero gratuito?",
                a: "Sì. La prima consulenza presso lo Studio di Piacenza è sempre gratuita e finalizzata a valutare la documentazione (lettera di licenziamento, contratto, buste paga) e fornire un parere preliminare. Solo dopo, e con il consenso del cliente, l'Avvocato formula un preventivo trasparente sulla base dei parametri forensi (D.M. 55/2014).",
            },
            {
                q: "Come funziona la conciliazione presso l'ITL di Piacenza?",
                a: "L'Ispettorato Territoriale del Lavoro di Piacenza offre un procedimento gratuito e riservato per la conciliazione delle controversie di lavoro. La domanda è presentata tramite un modulo dedicato; segue una convocazione di entrambe le parti davanti al conciliatore. In caso di accordo, il verbale ha valore di titolo esecutivo. Lo Studio assiste il cliente in tutte le fasi.",
            },
        ],
    },
    milano: {
        slug: "milano",
        name: "Milano",
        region: "Lombardia",
        path: "/avvocato-lavoro-milano",
        tel: "327 0345624",
        address: "Milano (MI) — ricevimento solo su appuntamento",
        hours: "Su appuntamento — anche in modalità remota",
        areaServed: ["Monza", "Bergamo", "Como", "Varese", "Pavia"],
        intro:
            "Lo Studio opera anche a Milano, ricevendo i clienti su appuntamento per assistenza specialistica in ogni ambito giuslavoristico. Particolare esperienza in vertenze di dirigenza e ristrutturazioni aziendali.",
        title: "Diritto del lavoro a Milano: consulenza riservata, su appuntamento.",
        metaDescription:
            "Avvocato giuslavorista a Milano: licenziamento dirigenti, mobbing, contratti di lavoro. Studio Avv. Marco Candido. Ricevimento su appuntamento. Tel 327 0345624.",
        body: [
            "Lo Studio dell'Avv. Marco Candido opera regolarmente a <strong>Milano</strong>, con ricevimento <strong>solo su appuntamento</strong>, per offrire ai clienti del territorio lombardo un servizio specialistico in diritto del lavoro. La città rappresenta il principale snodo per le vertenze giuslavoristiche in Italia, con il Tribunale del Lavoro più grande e attivo del paese.",
            "Milano è particolarmente rilevante per le pratiche di <strong>diritto del lavoro applicato alla dirigenza</strong>: indennità supplementari, patto di non concorrenza, contenzioso sui bonus e MBO, licenziamenti del management, transazioni novative. Lo Studio assiste anche aziende multinazionali in operazioni di ristrutturazione, licenziamenti collettivi e negoziazioni con le rappresentanze sindacali.",
            "Per esigenze di urgenza è possibile organizzare incontri in modalità <strong>remota (videoconferenza)</strong>, preservando la stessa qualità di analisi della documentazione e della strategia difensiva. La prima consulenza è sempre gratuita.",
        ],
        services: [
            "Licenziamento dirigenti e quadri",
            "Patto di non concorrenza",
            "Contenzioso bonus e MBO",
            "Ristrutturazioni aziendali",
        ],
        specializations: [
            {
                title: "Dirigenza e management",
                desc: "Milano è la capitale delle vertenze di dirigenza: indennità supplementari CCNL Dirigenti Industria/Terziario, patto di non concorrenza post-cessazione, licenziamento ad nutum, contenzioso su bonus discrezionali e piani MBO. Assistiamo dirigenti sia in fase pre-vertenziale (negoziazione dell'uscita) sia in contenzioso.",
            },
            {
                title: "Multinazionali e ristrutturazioni",
                desc: "Consulenza a gruppi multinazionali su procedure di licenziamento collettivo ex L. 223/1991, trasferimenti d'azienda ex art. 2112 c.c., accordi di secondo livello e negoziazioni sindacali. Esperienza consolidata nei settori bancario, farmaceutico, ICT e consulting.",
            },
            {
                title: "Mobbing e discriminazione nel corporate",
                desc: "A Milano il mobbing colpisce particolarmente ruoli middle-management in contesti di riorganizzazione aggressiva. Assistiamo nella costruzione del compendio probatorio (email, valutazioni, testimonianze) e nell'azione risarcitoria davanti al Tribunale del Lavoro di Milano.",
            },
            {
                title: "Contratti di collaborazione e partita IVA",
                desc: "Milano concentra il maggior numero di rapporti in area grigia tra lavoro autonomo e subordinato. Valutiamo la corretta qualificazione dei rapporti (co.co.co., partita IVA mono-committente, agenti, contratti di collaborazione) e i rischi di riqualificazione ex D.Lgs. 81/2015.",
            },
        ],
        reasonsIntro: "L'operatività dello Studio a Milano risponde alla domanda di consulenza specialistica in un mercato del lavoro complesso, dove le vertenze dirigenziali e le ristrutturazioni aziendali richiedono un know-how che non tutti gli studi generalisti hanno.",
        reasons: [
            {
                title: "Focus su lavoro dirigenziale",
                desc: "Specializzazione consolidata nel diritto del lavoro applicato al management: patti di non concorrenza, deferred bonus, stock option, indennità supplementari CCNL Dirigenti — con esperienza in cause davanti al Tribunale di Milano Sezione Lavoro.",
            },
            {
                title: "Approccio riservato e strategico",
                desc: "Milano richiede riservatezza assoluta: negoziazioni pre-vertenziali con controparti multinazionali, transazioni novative, exit strategy per dirigenti. Preserviamo l'immagine del cliente e cerchiamo sempre la soluzione più efficiente prima del contenzioso.",
            },
            {
                title: "Modalità remota per esigenze urgenti",
                desc: "Per lavoratori e aziende milanesi con agenda serrata, offriamo consulenze in videoconferenza con la stessa qualità del ricevimento in presenza. Ideale per dirigenti in servizio che vogliono valutare un'uscita.",
            },
            {
                title: "Rete di collaborazione con fiscalisti e giuslavoristi",
                desc: "Per pratiche complesse (transazioni con componenti fiscali, welfare aziendale, previdenza integrativa) collaboriamo con commercialisti e consulenti del lavoro milanesi di riferimento, per un approccio integrato al problema.",
            },
        ],
        tribunal: {
            h2: "Il Tribunale del Lavoro di Milano",
            paragraphs: [
                "Il <strong>Tribunale del Lavoro di Milano</strong> è il più grande d'Italia per volume di contenzioso, con oltre 15.000 cause pendenti in ogni momento. Le sezioni lavoro (che sono 6-8 in base al periodo) trattano ogni giorno controversie individuali di lavoro, cause previdenziali e ricorsi Fornero.",
                "I tempi medi di primo grado a Milano sono di <strong>12-18 mesi</strong>, decisamente più lunghi rispetto a fori minori come Piacenza o Lecce, a causa dell'elevato carico di lavoro. Tuttavia il Tribunale di Milano è considerato un giudice altamente specializzato: le decisioni sono spesso citate come precedenti nazionali, in particolare in materia di dirigenza e ristrutturazioni.",
                "Le decisioni sono appellabili davanti alla <strong>Corte d'Appello di Milano</strong> — Sezione Lavoro. Le cd. Tabelle di Milano rappresentano il riferimento nazionale per la liquidazione del danno biologico, morale ed esistenziale (utilizzate anche fuori dal foro ambrosiano).",
                "In alternativa al contenzioso davanti al Tribunale, lo Studio valuta sempre la possibilità di <strong>conciliazione presso l'Ispettorato Interregionale del Lavoro di Milano</strong> o presso le sedi sindacali, spesso preferibile in ottica di rapidità e riservatezza.",
            ],
            address: "Via San Barnaba, 50 — 20122 Milano (MI) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Come si prende appuntamento con lo Studio Candido a Milano?",
                a: "Il ricevimento a Milano è esclusivamente su appuntamento. Puoi contattare lo Studio al numero 327 0345624 o via email a marco.candido@pabclex.it per fissare un incontro in presenza o in videoconferenza. La prima consulenza è sempre gratuita.",
            },
            {
                q: "Lo Studio segue vertenze di dirigenza a Milano?",
                a: "Sì, è una delle nostre specializzazioni. Assistiamo dirigenti industriali, del terziario e del credito in tutte le vertenze: applicazione dell'indennità supplementare, patto di non concorrenza, contenzioso su bonus e MBO, licenziamento ad nutum, transazioni novative in sede protetta.",
            },
            {
                q: "Assistete anche aziende con sede a Milano?",
                a: "Sì. Lo Studio fornisce consulenza continuativa a PMI e multinazionali con sede a Milano, in materia di gestione del personale, licenziamenti collettivi ex L. 223/1991, procedure disciplinari, accordi sindacali di secondo livello, welfare aziendale. È possibile un rapporto di outsourcing legale su base annuale.",
            },
            {
                q: "Quanto durano le cause di lavoro al Tribunale di Milano?",
                a: "I tempi medi di primo grado sono 12-18 mesi. Nelle cause urgenti (es. reintegrazione da licenziamento discriminatorio) il rito Fornero prevede tempi ridotti nella fase sommaria. Per accorciare i tempi valutiamo sempre la fattibilità di una conciliazione stragiudiziale.",
            },
            {
                q: "Come funziona il patto di non concorrenza per un dirigente milanese?",
                a: "Il patto di non concorrenza post-cessazione (art. 2125 c.c.) deve essere scritto, avere durata determinata (max 5 anni per dirigenti, 3 per gli altri), limiti territoriali e di oggetto specifici, e prevedere un corrispettivo congruo. Se manca uno solo di questi elementi è nullo. Lo Studio valuta la validità dei patti e il rispetto delle clausole.",
            },
            {
                q: "Lo Studio si occupa di transazioni novative e ECE?",
                a: "Sì. La transazione novativa in sede ECE (Ente di Certificazione Ex art. 76 D.lgs. 276/2003) è lo strumento più utilizzato a Milano per definire in modo irreversibile ogni pretesa del lavoratore. Assistiamo nella redazione dell'accordo, nella verifica della clausola conciliativa e nella firma davanti alla commissione.",
            },
        ],
    },
    lecce: {
        slug: "lecce",
        name: "Lecce",
        region: "Puglia",
        path: "/avvocato-lavoro-lecce",
        tel: "327 0345624",
        address: "Lecce (LE) — ricevimento solo su appuntamento",
        hours: "Su appuntamento — anche in modalità remota",
        areaServed: ["Brindisi", "Taranto", "Bari"],
        intro:
            "Lo Studio è presente anche a Lecce, ricevendo i clienti su appuntamento per la tutela di lavoratori e imprese del territorio salentino e pugliese. Prima consulenza sempre gratuita.",
        title: "Avvocato del lavoro a Lecce e nel Salento.",
        metaDescription:
            "Avvocato del lavoro a Lecce e provincia: licenziamenti, mobbing, NASpI, contratti stagionali e turismo. Studio Avv. Marco Candido, ricevimento su appuntamento. Tel 327 0345624.",
        body: [
            "Lo Studio opera nel <strong>Foro di Lecce</strong>, avvalendosi di colleghi esperti in materia, con ricevimento <strong>solo su appuntamento</strong>, per assistere sia lavoratori che aziende del Salento e della Puglia meridionale.",
            "Il radicamento sul territorio salentino consente un'attenzione particolare alle specificità del <strong>tessuto produttivo locale</strong> — turismo, agricoltura, ristorazione, terziario e produzioni artigianali — e ai contratti collettivi di settore prevalenti in Puglia. Lo Studio è particolarmente attivo in vertenze del settore turistico-alberghiero, molto sviluppato nella provincia di Lecce con oltre 3.500 strutture ricettive.",
            "È possibile fissare un primo incontro conoscitivo, <strong>sempre gratuito</strong>, in presenza a Lecce oppure in modalità remota. Lo Studio fornisce assistenza completa: dalla fase stragiudiziale fino all'eventuale contenzioso davanti al Tribunale del Lavoro di Lecce, alla Corte d'Appello di Lecce e in Cassazione.",
        ],
        services: [
            "Vertenze di lavoro nel Foro di Lecce",
            "NASpI e contenzioso INPS",
            "Mobbing e infortuni sul lavoro",
            "Contratti stagionali e turismo",
        ],
        specializations: [
            {
                title: "Contratti stagionali e turismo",
                desc: "Il Salento vive una forte stagionalità del lavoro turistico. Lo Studio assiste stagionali e datori nella corretta applicazione del CCNL Turismo (Federalberghi/Filcams-Fisascat-UilTuCS), nella regolarizzazione dei contratti a termine, nella liquidazione del TFR e nella richiesta della NASpI stagionale.",
            },
            {
                title: "NASpI e contenzioso INPS",
                desc: "Nel Sud Italia il contenzioso previdenziale è più frequente per la maggior stagionalità dell'occupazione. Assistiamo nel calcolo NASpI, ricorsi amministrativi contro dinieghi INPS, riconoscimento delle giornate contributive nelle attività stagionali agricole e turistiche.",
            },
            {
                title: "Agricoltura e OTD",
                desc: "Il Salento è tra le zone italiane con maggior numero di operai a tempo determinato (OTD) agricoli. Assistenza in materia di iscrizione elenchi anagrafici INPS, regolarizzazione contrattuale, dispute salariali e diritti previdenziali (integrazione salariale, malattia, maternità agricola).",
            },
            {
                title: "Mobbing nel settore pubblico e sanitario",
                desc: "Il Salento ha una forte presenza di occupazione pubblica (ASL, enti locali, università). Lo Studio è specializzato in vertenze di mobbing e demansionamento nel comparto sanità, con particolare attenzione al ruolo del Consigliere di Parità e dell'Ispettorato del Lavoro.",
            },
        ],
        reasonsIntro: "Lo Studio è presente a Lecce con l'obiettivo di portare un livello di specializzazione elevato al servizio del territorio salentino, che merita professionisti focalizzati esclusivamente sulla materia giuslavoristica.",
        reasons: [
            {
                title: "Conoscenza del tessuto pugliese",
                desc: "Comprendiamo le specificità del mercato del lavoro salentino: forte stagionalità, importanza del turismo e dell'agricoltura, presenza di lavoro pubblico. Adattiamo la strategia legale al contesto socio-economico locale.",
            },
            {
                title: "Presenza fisica + remoto",
                desc: "Ricevimento su appuntamento a Lecce con collaboratori del posto per la fase locale delle cause. Modalità remota (videoconferenza) sempre disponibile per esigenze di rapidità o distanza geografica.",
            },
            {
                title: "Contenzioso previdenziale",
                desc: "Esperienza specifica in ricorsi INPS su NASpI, disoccupazione agricola, integrazione salariale, indennità di malattia e maternità per lavoratori stagionali. Assistiamo nel ricorso amministrativo (entro 90 gg) e in quello giudiziale davanti al Tribunale di Lecce.",
            },
            {
                title: "Prima consulenza gratuita",
                desc: "Come nelle altre sedi, il primo incontro conoscitivo è sempre gratuito. Prima di qualsiasi attività lo Studio fornisce un parere sulla fondatezza della pretesa e un preventivo forense trasparente.",
            },
        ],
        tribunal: {
            h2: "Il Tribunale del Lavoro di Lecce",
            paragraphs: [
                "Il <strong>Tribunale del Lavoro di Lecce</strong> — sezione specializzata del Tribunale Ordinario — è competente per le controversie individuali di lavoro e previdenziali della provincia salentina. Copre una popolazione di oltre 780.000 abitanti e vede un elevato numero di cause in ragione della forte occupazione stagionale del territorio.",
                "I tempi medi di primo grado a Lecce sono generalmente <strong>più contenuti rispetto alla media nazionale</strong>, tra i 6 e i 12 mesi, grazie a una sezione lavoro attiva e all'ampio ricorso alla conciliazione stragiudiziale. Le controversie previdenziali INPS e le vertenze del settore turistico-alberghiero rappresentano la maggior parte del contenzioso.",
                "Le decisioni sono appellabili davanti alla <strong>Corte d'Appello di Lecce</strong> — Sezione Lavoro. In alternativa al giudizio, lo Studio valuta sempre la conciliazione presso l'<strong>Ispettorato Territoriale del Lavoro di Lecce</strong> (competente anche per Brindisi e Taranto in alcune materie).",
                "Il territorio salentino è caratterizzato da una forte presenza sindacale (CGIL, CISL, UIL) e datoriale (Confindustria Lecce, Federalberghi Puglia). Lo Studio ha relazioni consolidate con le principali associazioni per la definizione di accordi stragiudiziali.",
            ],
            address: "Viale Michele De Pietro, 1 — 73100 Lecce (LE) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Come si prende appuntamento con lo Studio a Lecce?",
                a: "Il ricevimento a Lecce è su appuntamento. Contatta lo Studio al numero 327 0345624 o via email a marco.candido@pabclex.it. È possibile un incontro in presenza a Lecce oppure una videoconferenza. La prima consulenza è sempre gratuita.",
            },
            {
                q: "Lo Studio segue le vertenze del settore turistico-alberghiero salentino?",
                a: "Sì, è una delle nostre specializzazioni sul territorio pugliese. Assistiamo stagionali di alberghi, ristoranti, stabilimenti balneari e strutture ricettive nella verifica dei contratti (CCNL Turismo), nella liquidazione del TFR e delle indennità di fine rapporto, nella richiesta di NASpI stagionale.",
            },
            {
                q: "Come impugnare un licenziamento a Lecce?",
                a: "Il termine è di 60 giorni dalla ricezione del licenziamento per l'impugnazione scritta (raccomandata A/R, PEC), seguito da 180 giorni per il deposito del ricorso al Tribunale del Lavoro di Lecce o per la conciliazione. Sono termini perentori: contatta subito lo Studio per un parere preliminare gratuito.",
            },
            {
                q: "Assistete lavoratori agricoli del Salento?",
                a: "Sì. Il Salento ha una forte occupazione agricola con contratti OTD (operai a tempo determinato). Assistiamo nella verifica dell'iscrizione agli elenchi anagrafici INPS, nella regolarizzazione contrattuale, nel riconoscimento delle giornate contributive e nel contenzioso su indennità di disoccupazione agricola.",
            },
            {
                q: "Lo Studio si occupa di NASpI per stagionali a Lecce?",
                a: "Sì. La NASpI stagionale è cruciale per i lavoratori del turismo salentino. Verifichiamo il rispetto dei requisiti (13 settimane di contributi nei 4 anni + 30 giorni di lavoro effettivo negli ultimi 12 mesi), calcoliamo l'importo dovuto e assistiamo nei ricorsi contro dinieghi o riduzioni INPS.",
            },
            {
                q: "Assistete anche aziende con sede in Puglia?",
                a: "Sì. Lo Studio offre consulenza continuativa a PMI pugliesi in materia di gestione del personale, contratti di lavoro, procedure disciplinari, licenziamenti individuali e collettivi, accordi sindacali. Particolare esperienza nei settori turismo, agricoltura, artigianato e commercio.",
            },
        ],
    },
    cremona: {
        slug: "cremona",
        name: "Cremona",
        region: "Lombardia",
        path: "/avvocato-lavoro-cremona",
        tel: "0523 468737",
        address: "Foro competente: Tribunale di Cremona · Sede di riferimento a Piacenza (~40 km)",
        hours: "Consulenza previo appuntamento",
        areaServed: ["Crema", "Casalmaggiore", "Soresina", "Piacenza"],
        intro:
            "L'Avv. Marco Candido offre consulenza personalizzata in materia giuslavoristica a lavoratori e aziende della provincia di Cremona.",
        title: "Assistenza giuslavoristica a Cremona e provincia.",
        metaDescription:
            "Avvocato del lavoro a Cremona: licenziamenti, mobbing, vertenze, contratti. Studio Avv. Marco Candido — sede principale a Piacenza (40 km). Tel 0523 468737.",
        body: [],
        services: [
            "Licenziamenti nel comparto agroalimentare e metalmeccanico",
            "Vertenze retributive e differenze di TFR",
            "Contenzioso INPS e INAIL",
            "Assistenza a PMI del territorio",
        ],
        specializations: [
            {
                title: "Licenziamenti nel distretto agroalimentare",
                desc: "Impugnazione di licenziamenti disciplinari e per motivo oggettivo nel food (caseifici, salumifici, cantine), con particolare attenzione alla contrattazione collettiva dell'industria alimentare e al CCNL Cooperative Sociali.",
            },
            {
                title: "Vertenze in agricoltura e allevamento",
                desc: "Assistenza nei rapporti di lavoro agricolo (operai agricoli a tempo determinato e indeterminato), calcolo NASpI agricola, contenzioso su indennità di malattia INPS agricola, ricorsi contro dinieghi ITL.",
            },
            {
                title: "Ristrutturazioni nel metalmeccanico cremonese",
                desc: "Consulenza a PMI in operazioni di riorganizzazione, licenziamenti collettivi ex L. 223/1991, accordi sindacali di secondo livello, gestione della cassa integrazione ordinaria e straordinaria.",
            },
            {
                title: "Contenzioso disciplinare",
                desc: "Difesa in procedimenti disciplinari e impugnazione di sanzioni conservative e espulsive. Assistenza tempestiva entro i 5 giorni previsti dall'art. 7 St.Lav. per le controdeduzioni.",
            },
        ],
        reasonsIntro: "Lo Studio assiste da anni lavoratori e imprese del Cremonese con la stessa serietà riservata alla clientela della sede principale. Ecco perché sceglierci.",
        reasons: [
            {
                title: "Vicinanza operativa",
                desc: "La sede principale di Piacenza è a 40 km da Cremona (30 minuti via A21). Gli appuntamenti possono essere fissati in Studio o, se necessario, direttamente presso l'azienda o il lavoratore.",
            },
            {
                title: "Rete di colleghi esperti",
                desc: "L'Avv. Candido si avvale della collaborazione di colleghi esperti per la gestione e l'assistenza giudiziale della pratica, garantendo continuità di presenza in udienza e supporto multidisciplinare quando la complessità del caso lo richiede.",
            },
            {
                title: "Conoscenza del Foro di Cremona",
                desc: "Frequentazione costante del Tribunale del Lavoro di Cremona e della Corte d'Appello di Brescia. Conoscenza degli orientamenti locali della sezione lavoro e dei tempi processuali reali.",
            },
            {
                title: "Prima consulenza gratuita",
                desc: "L'incontro conoscitivo iniziale è sempre gratuito. Preventivo forense trasparente basato su parametri D.M. 55/2014 e possibilità di piani di pagamento agevolati.",
            },
        ],
        tribunal: {
            h2: "Il Tribunale del Lavoro di Cremona",
            paragraphs: [
                "Il <strong>Tribunale di Cremona</strong> — sezione lavoro — è competente per tutte le controversie individuali di lavoro e previdenza sui rapporti sorti nel territorio provinciale. Si applicano il rito del lavoro (art. 409 e ss. c.p.c.) per le controversie ordinarie e il rito Fornero (L. 92/2012) o rito ordinario per i licenziamenti a seconda del regime.",
                "Cremona è un tribunale di medie dimensioni: i tempi processuali sono generalmente rapidi in primo grado (6-12 mesi in media), specialmente per le cause di lavoro rispetto al civile ordinario. Le decisioni sono appellabili davanti alla <strong>Corte d'Appello di Brescia</strong> (Sezione Lavoro).",
                "Molte controversie vengono risolte in via stragiudiziale presso l'<strong>ITL di Cremona</strong> (art. 410 c.p.c.) o presso le sedi sindacali competenti (CGIL, CISL, UIL), con l'assistenza dell'avvocato per la validità del verbale come titolo esecutivo.",
                "La contrattazione collettiva applicabile prevalente nel Cremonese è quella dell'<strong>industria alimentare</strong>, del <strong>metalmeccanico</strong> e dell'<strong>agricoltura</strong>: la conoscenza specifica dei CCNL di settore è essenziale per una difesa efficace.",
            ],
            address: "Via dei Tribunali Vecchi, 5 — 26100 Cremona (CR) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Serve venire a Piacenza per il primo appuntamento?",
                a: "No. Per i clienti di Cremona e provincia il primo colloquio può essere fissato online (videocall Google Meet) o telefonicamente. Se lo preferite, l'Avv. Candido può ricevervi personalmente presso lo Studio di Piacenza (30 minuti da Cremona via A21) o, per pratiche di particolare complessità, presso l'azienda o il lavoratore.",
            },
            {
                q: "Il Tribunale di Cremona ha tempi rapidi per le cause di lavoro?",
                a: "Sì, rispetto ai grandi tribunali del Nord (Milano, Torino, Bologna) i tempi sono mediamente più contenuti: 6-12 mesi in primo grado, salvo istruttoria testimoniale complessa o CTU. È un vantaggio significativo per il lavoratore che ha bisogno di ottenere rapidamente il pagamento del dovuto.",
            },
            {
                q: "Quali settori economici cremonesi seguite maggiormente?",
                a: "L'esperienza principale è nel comparto agroalimentare (caseifici, salumifici, cioccolato, cantine), nella metalmeccanica cremonese (fonderie, macchine agricole, carpenteria) e nel terziario. Assistiamo sia le imprese sia i dipendenti, con approccio specialistico per ogni settore.",
            },
            {
                q: "Assistete anche in cause davanti alla Corte d'Appello di Brescia?",
                a: "Sì. La Corte d'Appello di Brescia è la sede naturale del secondo grado per le cause di lavoro del distretto cremonese. Lo Studio segue costantemente giudizi d'appello ed è disponibile anche a subentrare in cause avviate da altri legali.",
            },
            {
                q: "Come si calcolano le indennità in agricoltura?",
                a: "L'agricoltura ha regole specifiche per NASpI, malattia, maternità e infortunio (INPS Gestione Agricoltura, INAIL Settore Agricoltura). Il calcolo si basa sulle giornate lavorate e sulla retribuzione convenzionale annuale. Assistiamo nella ricostruzione della posizione contributiva e nel ricorso amministrativo o giudiziale contro INPS/INAIL.",
            },
            {
                q: "Il primo incontro è davvero gratuito?",
                a: "Sì, sempre. Portate con voi la documentazione essenziale (lettera di licenziamento, contratto, buste paga degli ultimi 12 mesi, eventuali comunicazioni disciplinari). L'Avv. Candido valuta la fondatezza della pretesa e fornisce un parere preliminare gratuito, con preventivo scritto per l'eventuale attività successiva.",
            },
        ],
    },
    pavia: {
        slug: "pavia",
        name: "Pavia",
        region: "Lombardia",
        path: "/avvocato-lavoro-pavia",
        tel: "0523 468737",
        address: "Foro competente: Tribunale di Pavia · Sede di riferimento a Piacenza (~60 km)",
        hours: "Consulenza previo appuntamento",
        areaServed: ["Vigevano", "Voghera", "Stradella", "Lomellina", "Piacenza"],
        intro:
            "L'Avv. Marco Candido offre consulenza personalizzata in materia giuslavoristica a lavoratori e aziende della provincia di Pavia.",
        title: "Assistenza giuslavoristica a Pavia e provincia.",
        metaDescription:
            "Avvocato del lavoro a Pavia: licenziamenti, mobbing, vertenze, contratti. Studio Avv. Marco Candido — sede a Piacenza (60 km). Tel 0523 468737.",
        body: [],
        services: [
            "Licenziamenti nel farmaceutico e chimico",
            "Mobbing e stress lavoro-correlato",
            "Vertenze retributive e differenze",
            "Assistenza dirigenti industriali",
        ],
        specializations: [
            {
                title: "Contenzioso nel farmaceutico e chimico",
                desc: "Assistenza a ricercatori, dirigenti e operai del comparto farmaceutico e chimico pavese: licenziamenti disciplinari e per motivo oggettivo, tutela della riservatezza brevettuale, patti di non concorrenza post-contrattuali.",
            },
            {
                title: "Mobbing e straining nel terziario",
                desc: "Casistica frequente nelle strutture ospedaliere pavesi (San Matteo, Maugeri) e negli enti pubblici: raccolta prove documentali, testimoniali e mediche, con collaborazione consolidata con medici legali e psichiatri.",
            },
            {
                title: "Vertenze nella calzatura vigevanese",
                desc: "Distretto storico della calzatura: assistenza in vertenze retributive, calcolo del TFR e delle mensilità aggiuntive, contenzioso su appalti e distacchi di manodopera con particolare esperienza sul CCNL Pelli e Cuoio.",
            },
            {
                title: "NASpI agricola in Lomellina",
                desc: "Verifica dei requisiti contributivi per la NASpI agricola nel comparto risicolo, ricorsi INPS contro dinieghi o riduzioni. Calcolo dell'indennità sulla base della retribuzione convenzionale annuale e delle giornate lavorate.",
            },
        ],
        reasonsIntro: "Lo Studio offre alla clientela pavese la stessa serietà e specializzazione riservata alla sede principale. Ecco cosa aspettarsi.",
        reasons: [
            {
                title: "Foro di Pavia frequentato costantemente",
                desc: "Presenza abituale al Tribunale del Lavoro di Pavia e conoscenza dei giudici della sezione lavoro. Le decisioni sono appellabili davanti alla Corte d'Appello di Milano — anch'essa foro consueto dello Studio.",
            },
            {
                title: "Rete di colleghi esperti",
                desc: "L'Avv. Candido si avvale della collaborazione di colleghi esperti per la gestione e l'assistenza giudiziale della pratica, garantendo continuità di presenza in udienza e supporto multidisciplinare quando la complessità del caso lo richiede.",
            },
            {
                title: "Modalità flessibili di consulenza",
                desc: "Primo colloquio in videocall, telefonico o in Studio a Piacenza. Per pratiche complesse possibile appuntamento presso l'azienda o il lavoratore in provincia di Pavia. Tempi di risposta rapidi.",
            },
            {
                title: "Prima consulenza gratuita",
                desc: "L'incontro conoscitivo è sempre gratuito. Preventivo forense trasparente su base D.M. 55/2014 e possibilità di piani di pagamento personalizzati.",
            },
        ],
        tribunal: {
            h2: "Il Tribunale del Lavoro di Pavia",
            paragraphs: [
                "Il <strong>Tribunale di Pavia</strong> — sezione lavoro — è competente per tutte le controversie individuali di lavoro e previdenza sui rapporti sorti nella provincia. Le controversie ordinarie seguono il rito del lavoro (art. 409 e ss. c.p.c.); i licenziamenti seguono il rito ordinario o il rito Fornero (L. 92/2012) a seconda del regime applicabile.",
                "I tempi processuali a Pavia sono nella media dei tribunali lombardi: 8-14 mesi in primo grado per cause di lavoro standard. Le decisioni sono appellabili davanti alla <strong>Corte d'Appello di Milano</strong> — Sezione Lavoro, che ha carico ordinario elevato ma con tempi tecnici prevedibili.",
                "La conciliazione stragiudiziale è gestita dall'<strong>ITL di Pavia</strong> (art. 410 c.p.c.) e dalle sedi sindacali. Frequenti le conciliazioni presso le OO.SS. di categoria del comparto sanitario e chimico-farmaceutico, dove operiamo con esperienza consolidata.",
                "I settori giuslavoristici predominanti del pavese sono il <strong>chimico-farmaceutico</strong>, il <strong>sanitario</strong> (per la presenza di grandi poli come il San Matteo), l'<strong>industria calzaturiera</strong> e l'<strong>agricoltura risicola</strong> — ognuno con specificità normative e contrattuali diverse.",
            ],
            address: "Via Menocchio, 26 — 27100 Pavia (PV) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Serve venire a Piacenza per il primo appuntamento?",
                a: "No. Per i clienti di Pavia il primo colloquio è disponibile in videocall (Google Meet), telefonicamente o in Studio a Piacenza (60 km via A21/A7, circa 50 minuti). Per pratiche complesse è possibile fissare appuntamenti direttamente in provincia di Pavia.",
            },
            {
                q: "Quali orientamenti ha il Tribunale del Lavoro di Pavia?",
                a: "La sezione lavoro di Pavia ha orientamenti tendenzialmente allineati con la Corte d'Appello di Milano (giudice di secondo grado). Sensibilità particolare per il mobbing e le patologie di stress lavoro-correlato, oggetto di crescente casistica nel foro pavese.",
            },
            {
                q: "Seguite anche dirigenti farmaceutici e sanitari?",
                a: "Sì, con particolare esperienza. La tutela del dirigente ha peculiarità significative (recesso ad nutum ma con obbligo di giustificatezza CCNL, patti di non concorrenza, stock options, indennità di risultato). Assistiamo dirigenti di aziende farmaceutiche pavesi e di strutture sanitarie pubbliche e private.",
            },
            {
                q: "Come funziona la NASpI agricola in Lomellina?",
                a: "L'agricoltura risicola lomellina genera ampia casistica di lavoro agricolo stagionale. La NASpI agricola richiede 102 giornate contributive nel biennio precedente e retribuisce l'operaio agricolo per un periodo pari al 50% delle giornate lavorate. Assistiamo nel calcolo, nei ricorsi INPS e nel contenzioso previdenziale.",
            },
            {
                q: "Il primo incontro è davvero gratuito?",
                a: "Sì, sempre. La consulenza iniziale è finalizzata a valutare la fondatezza della pretesa e a fornire un orientamento chiaro. Solo dopo l'incontro conoscitivo l'Avvocato formula un preventivo scritto trasparente per l'eventuale attività successiva.",
            },
            {
                q: "Quanto costa impugnare un licenziamento a Pavia?",
                a: "Il costo dipende da fase (stragiudiziale, giudiziale, appello) e valore della causa (scaglione parametri forensi D.M. 55/2014). Un'impugnazione stragiudiziale tipica parte da poche centinaia di euro; il ricorso al Tribunale richiede in media 3-5.000 euro. Sono possibili patti di quota lite parziali e pagamenti dilazionati.",
            },
        ],
    },
    lodi: {
        slug: "lodi",
        name: "Lodi",
        region: "Lombardia",
        path: "/avvocato-lavoro-lodi",
        tel: "0523 468737",
        address: "Foro competente: Tribunale di Lodi · Sede di riferimento a Piacenza (~30 km)",
        hours: "Consulenza previo appuntamento",
        areaServed: ["Codogno", "Casalpusterlengo", "Sant'Angelo Lodigiano", "Piacenza"],
        intro:
            "L'Avv. Marco Candido offre consulenza personalizzata in materia giuslavoristica a lavoratori e aziende della provincia di Lodi.",
        title: "Assistenza giuslavoristica a Lodi e provincia.",
        metaDescription:
            "Avvocato del lavoro a Lodi: licenziamenti, mobbing, vertenze, logistica. Studio Avv. Marco Candido — sede principale a Piacenza (30 km). Tel 0523 468737.",
        body: [],
        services: [
            "Vertenze nella logistica e trasporto",
            "Licenziamenti nell'e-commerce",
            "Contenzioso su appalti e distacchi",
            "Assistenza al mondo cooperativo",
        ],
        specializations: [
            {
                title: "Contenzioso nella logistica lodigiana",
                desc: "Il Lodigiano ospita alcuni dei più grandi hub logistici d'Italia. Assistenza in vertenze retributive nella catena di appalti (CCNL Logistica, Trasporto e Spedizione), rivendicazioni sull'orario di lavoro, indennità di malattia e infortunio, tutela dei rider e degli autisti.",
            },
            {
                title: "Licenziamenti nell'e-commerce e distribuzione",
                desc: "Impugnazione di licenziamenti disciplinari nei magazzini automatizzati e nelle piattaforme di consegna, con particolare attenzione alle contestazioni per violazione degli obiettivi di produttività e monitoraggio degli standard performativi.",
            },
            {
                title: "Appalti irregolari e caporalato",
                desc: "Assistenza nei casi di interposizione illecita di manodopera (art. 29 D.Lgs. 276/2003) e caporalato (art. 603-bis c.p.). Riconoscimento della retribuzione del committente e responsabilità solidale per contributi e retribuzioni.",
            },
            {
                title: "Comparto caseario e alimentare",
                desc: "Consulenza a caseifici e salumifici lodigiani (Grana Padano DOP e formaggi tipici locali) su gestione del personale stagionale, procedure disciplinari e conciliazioni ITL.",
            },
        ],
        reasonsIntro: "Il Lodigiano è naturalmente vicino alla sede principale dello Studio. Ecco perché la scelta è particolarmente efficiente.",
        reasons: [
            {
                title: "Solo 30 km dalla sede principale",
                desc: "Lo Studio a Piacenza è distante 30 km via A1 (20 minuti). Questa vicinanza consente appuntamenti rapidi, intervento tempestivo su pratiche urgenti (impugnazione entro 60 gg) e presenza costante presso il Tribunale di Lodi.",
            },
            {
                title: "Rete di colleghi esperti",
                desc: "L'Avv. Candido si avvale della collaborazione di colleghi esperti per la gestione e l'assistenza giudiziale della pratica, garantendo continuità di presenza in udienza e supporto multidisciplinare quando la complessità del caso lo richiede.",
            },
            {
                title: "Foro di Lodi frequentato con regolarità",
                desc: "Presenza costante al Tribunale del Lavoro di Lodi e alla Corte d'Appello di Milano. Conoscenza degli orientamenti giurisprudenziali locali e dei tempi tecnici delle udienze.",
            },
            {
                title: "Prima consulenza gratuita",
                desc: "Colloquio conoscitivo iniziale sempre gratuito con parere trasparente e preventivo scritto. Possibilità di appuntamenti in Studio a Piacenza o presso l'azienda/il lavoratore nel Lodigiano.",
            },
        ],
        tribunal: {
            h2: "Il Tribunale del Lavoro di Lodi",
            paragraphs: [
                "Il <strong>Tribunale di Lodi</strong> — sezione lavoro — è competente per le controversie individuali di lavoro e previdenza sui rapporti sorti nella provincia. Si applicano il rito del lavoro (art. 409 e ss. c.p.c.) per le controversie ordinarie e il rito Fornero (L. 92/2012) o rito ordinario per i licenziamenti a seconda del regime.",
                "Lodi è un tribunale di medie dimensioni con tempi processuali complessivamente rapidi (6-12 mesi in primo grado per la sezione lavoro). La <strong>Corte d'Appello di Milano</strong> è il giudice del secondo grado — Sezione Lavoro, con carico elevato ma tempi prevedibili.",
                "La conciliazione stragiudiziale è gestita dall'<strong>ITL di Milano-Lodi</strong> (art. 410 c.p.c.) e dalle OO.SS. di categoria. Nella logistica sono frequenti le conciliazioni presso le sedi CGIL Filt, CISL Fit, UIL Trasporti con esperienza consolidata dello Studio.",
                "Il comparto giuslavoristicamente più rilevante del Lodigiano è la <strong>logistica</strong> (CCNL Logistica, Trasporto e Spedizione), seguito dall'<strong>industria alimentare</strong> (CCNL Alimentare) e dall'<strong>agricoltura</strong>.",
            ],
            address: "Via Solferino, 28 — 26900 Lodi (LO) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Quanto dista Lodi dalla sede principale dello Studio?",
                a: "Solo 30 km via A1 (uscita Casalpusterlengo o Lodi), circa 20 minuti in auto. Questa vicinanza consente appuntamenti rapidi, intervento tempestivo e presenza costante al Tribunale del Lavoro di Lodi.",
            },
            {
                q: "Seguite lavoratori del comparto logistico e i rider?",
                a: "Sì, con specifica esperienza. Il settore logistico lodigiano è tra i più importanti d'Italia e presenta problematiche tipiche: appalti irregolari, orario di lavoro, indennità di trasferta, tutela dei rider (D.Lgs. 81/2015 e riforma del CCNL). Assistiamo sia i singoli lavoratori sia le organizzazioni sindacali.",
            },
            {
                q: "Come funziona il contenzioso su appalti irregolari nella logistica?",
                a: "In caso di interposizione illecita di manodopera (art. 29 D.Lgs. 276/2003) il lavoratore può chiedere l'accertamento del rapporto di lavoro con il committente effettivo, con recupero delle differenze retributive e regolarizzazione contributiva. La materia è particolarmente frequente nel Lodigiano vista la struttura degli hub logistici.",
            },
            {
                q: "Il Tribunale di Lodi ha tempi rapidi per le cause di lavoro?",
                a: "Sì, mediamente 6-12 mesi in primo grado per cause di lavoro standard. È tra i tribunali più efficienti della Lombardia. Per le urgenze c'è sempre la possibilità di richiedere il rito Fornero (per licenziamenti in regime pre-Jobs Act) o misure cautelari ex art. 700 c.p.c.",
            },
            {
                q: "Assistete anche le imprese del Lodigiano?",
                a: "Sì. Lo Studio offre consulenza continuativa alle PMI lodigiane (in particolare della logistica, dell'e-commerce e del food) in materia di contratti di lavoro, procedure disciplinari, licenziamenti, ristrutturazioni, accordi sindacali di secondo livello e gestione delle vertenze stragiudiziali.",
            },
            {
                q: "Il primo incontro è davvero gratuito?",
                a: "Sì, sempre. Portate con voi la documentazione essenziale (lettera di licenziamento, contratto, buste paga, eventuali contestazioni). L'Avv. Candido valuta la fondatezza della pretesa e fornisce un parere preliminare senza impegno.",
            },
        ],
    },
    fidenza: {
        slug: "fidenza",
        name: "Fidenza",
        region: "Emilia-Romagna",
        path: "/avvocato-lavoro-fidenza",
        tel: "0523 468737",
        address: "Foro competente: Tribunale di Parma · Sede di riferimento a Piacenza (~30 km)",
        hours: "Consulenza previo appuntamento",
        areaServed: ["Salsomaggiore Terme", "Fontanellato", "Busseto", "Piacenza"],
        intro:
            "L'Avv. Marco Candido offre consulenza personalizzata in materia giuslavoristica a lavoratori e aziende del comprensorio di Fidenza.",
        title: "Assistenza giuslavoristica a Fidenza e Val di Taro.",
        metaDescription:
            "Avvocato del lavoro a Fidenza: licenziamenti, mobbing, food valley, vertenze. Studio Avv. Marco Candido — sede a Piacenza (30 km). Tel 0523 468737.",
        body: [],
        services: [
            "Vertenze nel comparto food e salumi",
            "Licenziamenti nel distretto agroalimentare",
            "Contenzioso su stagionalità e orari",
            "Assistenza PMI fidentine",
        ],
        specializations: [
            {
                title: "Contenzioso nel food e nella salumificazione",
                desc: "Assistenza a operai, addetti alla stagionatura, tecnologi alimentari e dirigenti dei salumifici e caseifici del fidentino. Applicazione del CCNL Industria Alimentare e conoscenza delle problematiche del comparto DOP e IGP.",
            },
            {
                title: "Stagionalità e contratti a termine",
                desc: "Frequenti le controversie su reiterazione di contratti a termine, mansioni e retribuzione degli stagionali, gestione delle indennità di fine rapporto e tutela previdenziale INPS.",
            },
            {
                title: "Appalti irregolari nel food",
                desc: "Interposizione illecita di manodopera nelle cooperative di servizi che operano nel comparto alimentare fidentino: accertamento del rapporto con il committente e recupero contributivo.",
            },
            {
                title: "Consulenza continuativa PMI del fidentino",
                desc: "Assistenza a imprese familiari del food e del commercio: contratti di lavoro, procedure disciplinari, licenziamenti, accordi sindacali, gestione della cassa integrazione ordinaria e straordinaria.",
            },
        ],
        reasonsIntro: "Fidenza è geograficamente vicina alla sede principale dello Studio e strategicamente collegata al foro di Parma. Ecco cosa aspettarsi.",
        reasons: [
            {
                title: "30 km dalla sede principale",
                desc: "La sede di Piacenza è a 30 km da Fidenza (uscita A1 Fidenza, 20 minuti). Appuntamenti rapidi in Studio o sul territorio per pratiche complesse. Videocall sempre disponibile.",
            },
            {
                title: "Frequentazione del Tribunale di Parma",
                desc: "L'Avv. Candido opera regolarmente davanti al Tribunale del Lavoro di Parma — foro competente per il fidentino — e alla Corte d'Appello di Bologna. Conoscenza degli orientamenti locali e dei tempi tecnici.",
            },
            {
                title: "Rete di colleghi esperti",
                desc: "L'Avv. Candido si avvale della collaborazione di colleghi esperti per la gestione e l'assistenza giudiziale della pratica, garantendo continuità di presenza in udienza e supporto multidisciplinare quando la complessità del caso lo richiede.",
            },
            {
                title: "Prima consulenza gratuita",
                desc: "Incontro conoscitivo iniziale sempre gratuito. Preventivo forense trasparente su base D.M. 55/2014 e piani di pagamento agevolati per le famiglie e i lavoratori.",
            },
        ],
        tribunal: {
            h2: "Il foro competente: Tribunale di Parma",
            paragraphs: [
                "Fidenza non ha un tribunale autonomo. Il foro competente per tutte le controversie di lavoro sorte nel comprensorio fidentino è il <strong>Tribunale di Parma</strong> — sezione lavoro, con applicazione del rito del lavoro (art. 409 e ss. c.p.c.) e del rito Fornero (L. 92/2012) per i licenziamenti pre-Jobs Act.",
                "Il Tribunale di Parma è uno dei principali tribunali dell'Emilia-Romagna: tempi processuali intorno agli 8-14 mesi in primo grado per cause di lavoro standard. Le decisioni sono appellabili davanti alla <strong>Corte d'Appello di Bologna</strong> — Sezione Lavoro.",
                "La conciliazione stragiudiziale è gestita dall'<strong>ITL di Parma</strong> (sede di Fidenza per la ricezione delle istanze, art. 410 c.p.c.) e dalle sedi sindacali di categoria. Nel comparto agroalimentare fidentino sono frequenti le conciliazioni presso FAI-CISL, FLAI-CGIL e UILA-UIL.",
                "Il tessuto giuslavoristico fidentino è dominato dall'<strong>industria alimentare</strong> (CCNL Alimentare Industria e CCNL Alimentare Cooperative), con casistica significativa su stagionalità, appalti e contratti a termine.",
            ],
            address: "Piazzale Santafiora, 1 — 43121 Parma (PR) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Fidenza ha un tribunale autonomo?",
                a: "No. Fidenza è comune della provincia di Parma e il foro competente per le controversie di lavoro sorte nel comprensorio fidentino è il Tribunale del Lavoro di Parma. Le sedi sindacali locali (Fidenza, Salsomaggiore) mantengono comunque uno sportello per la conciliazione stragiudiziale.",
            },
            {
                q: "Quanto dista Fidenza dalla sede dello Studio a Piacenza?",
                a: "30 km via A1 (uscita Fidenza), circa 20 minuti. Questa vicinanza consente appuntamenti rapidi in Studio o direttamente sul territorio per pratiche complesse. In alternativa è disponibile il colloquio in videocall.",
            },
            {
                q: "Seguite le vertenze nel comparto salumi e food?",
                a: "Sì, con esperienza consolidata. Il fidentino è cuore della Food Valley (Prosciutto di Parma DOP, Culatello di Zibello DOP, Salame Felino IGP) e presenta casistica specifica su stagionalità, tecnici della stagionatura, veterinari aziendali, controlli qualità. Assistiamo operai, tecnici, dirigenti e aziende.",
            },
            {
                q: "Come funziona la stagionalità nel food fidentino?",
                a: "Il food valley ha ritmi produttivi legati alle stagioni agricole (raccolta pomodoro, uva, tartufi) e ai cicli di stagionatura (prosciutti, salumi). I contratti a termine e la reiterazione dei rapporti sono frequenti e possono generare vertenze per riqualificazione del rapporto a tempo indeterminato, con conseguente diritto al risarcimento (12 mensilità ex art. 32 L. 183/2010).",
            },
            {
                q: "Assistete anche le PMI fidentine come consulenza continuativa?",
                a: "Sì. Lo Studio offre consulenza a piccole e medie imprese del comparto agroalimentare, commercio e turismo termale (Salsomaggiore) in materia di gestione del personale, contratti, procedure disciplinari, licenziamenti individuali e collettivi, cassa integrazione, accordi sindacali di secondo livello.",
            },
            {
                q: "Il primo incontro è davvero gratuito?",
                a: "Sì, sempre. Portate documentazione essenziale (lettera di licenziamento, contratto, buste paga ultimi 12 mesi, eventuali comunicazioni disciplinari). L'Avv. Candido fornisce un parere preliminare gratuito, con preventivo scritto per l'eventuale attività successiva.",
            },
        ],
    },
    "fiorenzuola-darda": {
        slug: "fiorenzuola-darda",
        name: "Fiorenzuola d'Arda",
        region: "Emilia-Romagna",
        path: "/avvocato-lavoro-fiorenzuola-darda",
        tel: "0523 468737",
        address: "Foro competente: Tribunale di Piacenza · Sede principale a Piacenza (~25 km)",
        hours: "Consulenza previo appuntamento",
        areaServed: ["Cortemaggiore", "San Giorgio Piacentino", "Alseno", "Pontenure", "Piacenza"],
        intro:
            "L'Avv. Marco Candido offre consulenza personalizzata in materia giuslavoristica a lavoratori e aziende di Fiorenzuola d'Arda e del basso piacentino.",
        title: "Assistenza giuslavoristica a Fiorenzuola d'Arda.",
        metaDescription:
            "Avvocato del lavoro a Fiorenzuola d'Arda: licenziamenti, mobbing, vertenze, logistica, metalmeccanica. Studio Avv. Marco Candido — sede a Piacenza. Tel 0523 468737.",
        body: [],
        services: [
            "Licenziamenti nel metalmeccanico",
            "Vertenze nella logistica",
            "Contenzioso nel food",
            "Mobbing e demansionamento",
        ],
        specializations: [
            {
                title: "Licenziamenti nel metalmeccanico fiorenzuolano",
                desc: "Assistenza in impugnazioni disciplinari e per motivo oggettivo nelle grandi aziende del distretto (macchine agricole, carpenteria pesante, componentistica). Applicazione del CCNL Metalmeccanico Industria e conoscenza delle riorganizzazioni tipiche del settore.",
            },
            {
                title: "Contenzioso logistico lungo l'A1",
                desc: "Il basso piacentino ospita rilevanti hub logistici. Vertenze retributive in appalti (CCNL Logistica), tutela dei corrieri e degli autisti, gestione dell'orario di lavoro multi-turno e delle indennità di trasferta.",
            },
            {
                title: "Mobbing e straining industriale",
                desc: "Casistica consolidata di mobbing verticale e orizzontale nelle strutture industriali fiorenzuolane. Costruzione del compendio probatorio con collaborazione di medici legali, psichiatri e specialisti del comportamento organizzativo.",
            },
            {
                title: "Ristrutturazioni e crisi aziendali",
                desc: "Assistenza a lavoratori e sindacati in operazioni di riorganizzazione, licenziamenti collettivi ex L. 223/1991, procedure di CIGO/CIGS, accordi di solidarietà e trasferimenti d'azienda ex art. 2112 c.c.",
            },
        ],
        reasonsIntro: "Fiorenzuola d'Arda rientra nel foro di Piacenza — sede principale dello Studio. Ecco perché questa è la scelta più naturale.",
        reasons: [
            {
                title: "Foro di Piacenza = sede dello Studio",
                desc: "Il Tribunale del Lavoro competente per Fiorenzuola d'Arda è quello di Piacenza, dove l'Avv. Candido ha la sede principale e opera quotidianamente. Massima conoscenza della sezione lavoro, dei giudici e dei tempi tecnici locali.",
            },
            {
                title: "25 km dallo Studio",
                desc: "Fiorenzuola dista 25 km dalla sede di Corso Vittorio Emanuele II 142/D (uscita A1 Fiorenzuola, 20 minuti). Appuntamenti disponibili in Studio, in videocall o direttamente sul territorio per pratiche complesse.",
            },
            {
                title: "Rete di colleghi esperti",
                desc: "L'Avv. Candido si avvale della collaborazione di colleghi esperti per la gestione e l'assistenza giudiziale della pratica, garantendo continuità di presenza in udienza e supporto multidisciplinare quando la complessità del caso lo richiede.",
            },
            {
                title: "Prima consulenza gratuita",
                desc: "L'incontro conoscitivo iniziale è sempre gratuito, in Studio o in videocall. Parere trasparente sulla fondatezza della pretesa e preventivo forense scritto su base D.M. 55/2014.",
            },
        ],
        tribunal: {
            h2: "Il foro di Fiorenzuola: Tribunale di Piacenza",
            paragraphs: [
                "Fiorenzuola d'Arda non ha un tribunale autonomo. Il foro competente per tutte le controversie di lavoro sorte nel territorio comunale e nell'area del basso piacentino è il <strong>Tribunale di Piacenza</strong> — sezione lavoro, dove lo Studio ha la sede principale e opera regolarmente.",
                "Il rito applicabile è quello del lavoro (art. 409 e ss. c.p.c.) per le controversie ordinarie, con applicazione del rito Fornero (L. 92/2012) o del rito ordinario per i licenziamenti a seconda del regime. I tempi processuali a Piacenza sono tra i più rapidi d'Italia: 4-12 mesi in primo grado.",
                "Le decisioni sono appellabili davanti alla <strong>Corte d'Appello di Bologna</strong> — Sezione Lavoro. Lo Studio segue con costanza anche i giudizi di secondo grado, con conoscenza approfondita degli orientamenti bolognesi.",
                "La conciliazione stragiudiziale è gestita dall'<strong>ITL di Piacenza</strong> (competente anche per Lodi) — art. 410 c.p.c. e dalle OO.SS. di categoria del metalmeccanico (FIM-CISL, FIOM-CGIL, UILM-UIL), del food (FAI-CISL, FLAI-CGIL, UILA-UIL) e della logistica (FIT-CISL, FILT-CGIL, UILTRASPORTI-UIL).",
            ],
            address: "Via Consiglio, 17 — 29121 Piacenza (PC) · Sezione Lavoro",
        },
        faqs: [
            {
                q: "Fiorenzuola d'Arda ha un tribunale autonomo?",
                a: "No. Il foro competente per Fiorenzuola d'Arda e per l'intera provincia di Piacenza è il Tribunale del Lavoro di Piacenza, dove lo Studio ha la sede principale. Questa coincidenza garantisce massima efficienza operativa nelle pratiche del fiorenzuolano.",
            },
            {
                q: "Quanto dista Fiorenzuola dalla sede dello Studio?",
                a: "25 km via A1 (uscita Fiorenzuola d'Arda), 20 minuti in auto. Gli appuntamenti possono essere fissati in Studio a Piacenza, in videocall (Google Meet) o direttamente presso l'azienda o il lavoratore quando la pratica lo richiede.",
            },
            {
                q: "Seguite le grandi aziende metalmeccaniche del fiorenzuolano?",
                a: "Sì. Il distretto metalmeccanico del basso piacentino (macchine agricole, carpenteria pesante, componentistica per automotive) è uno dei principali dell'Emilia occidentale. Assistiamo operai, impiegati tecnici, dirigenti e aziende in tutte le tipologie di contenzioso: licenziamenti, mobbing, vertenze retributive, contenzioso disciplinare.",
            },
            {
                q: "Quali tempi ha il Tribunale del Lavoro di Piacenza?",
                a: "Piacenza è tra i tribunali più rapidi d'Italia per le cause di lavoro: mediamente 4-12 mesi in primo grado. La ridotta pendenza consente istruttorie snelle e udienze ravvicinate. Un vantaggio significativo per chi cerca una rapida definizione della controversia.",
            },
            {
                q: "Come si struttura il primo incontro?",
                a: "Il primo colloquio è sempre gratuito e finalizzato a valutare la fondatezza della pretesa. Portate con voi documentazione essenziale (contratto, lettera di licenziamento, buste paga ultimi 12 mesi, eventuali comunicazioni scritte del datore). L'Avv. Candido fornisce un parere preliminare e un preventivo scritto per l'eventuale attività successiva.",
            },
            {
                q: "Offrite consulenza continuativa alle PMI del fiorenzuolano?",
                a: "Sì. Molte PMI del basso piacentino si avvalgono di consulenza continuativa dello Studio per gestione del personale, contratti di lavoro, procedure disciplinari, licenziamenti, ristrutturazioni, accordi sindacali di secondo livello e prevenzione del contenzioso.",
            },
        ],
    },
};
